<template>
	<div class="empty-state" v-if="!messages.length && !loading">
		{{ $t('groupsettings_mediatray_polls_none') }}
	</div>

	<div class="group-media-polls-container">
		<ScrollToLoad
			class="group-media-polls"
			:loading="loading"
			@bottom="loadMoreMessages"
		>
			<MediaItem
				v-for="poll in visibleItems"
				:item="poll"
				:key="poll.id"
				@click="onPollClick(poll)"
			>
				<div class="poll-card-stub">
					<div class="poll-icon-container">
						<SquarePollHorizontalIcon type="solid" />
					</div>
					<div class="poll-content">
						<div class="poll-title">{{ poll.question }}</div>

						<div class="poll-footer">
							<PollCloseState class="poll-expiration" :pollId="poll.id" />
							<div class="poll-vote-count">
								<UserIcon type="solid" />
								<span>{{ poll.total_vote_count }} votes</span>
							</div>
						</div>
					</div>
				</div>
			</MediaItem>

			<template #spinner>
				<IosSpinner
					v-if="loading"
					top="47%"
					left="0"
					right="0"
					style="margin-left: auto; margin-right: auto;"
				>
					<template #title>{{ $t('common_loader_loading_title') }}</template>
				</IosSpinner>
			</template>
		</ScrollToLoad>
	</div>

	<PollResults
		v-if="showResultsModal"
		:pollId="selectedPoll.id"
		@close="closeModal"
		@cancel="closeModal"
	/>

	<StandardModal
		v-if="showViewInChatModal"
		open
		:title="'Close Poll'"
		:confirmText="'View in Chat'"
		@confirm="viewInChat"
		@cancel="closeModal"
		small
		ref="viewInChatModal"
	>
		<div class="view-in-chat-modal-wrapper">
			<SquarePollHorizontalIcon halo class="view-in-chat-icon" type="regular" />
			<div class="help">
				{{ resultsLabel }}
			</div>
		</div>
	</StandardModal>
</template>

<script setup>
import { onMounted, computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { getCancelToken } from '@/helpers/api'
import { fetchHistoryWithoutStore } from '@/api/chat'
import MediaItem from '@/components/GroupSettings/MediaItem'
import { ScrollToLoad, useAlert, StandardModal } from '@/components/common'
import IosSpinner from '@/components/IosSpinner'
import { SquarePollHorizontalIcon, UserIcon } from '@/icons'
import PollCloseState from '@/components/Polls/PollCloseState'
import { setPoll } from '@/store/messages'
import PollResults from '@/components/Polls/PollResults'
import { canViewResults } from '@/helpers/polls'

const router = useRouter()
const VISIBLE_PAGE_SIZE = 24

const props = defineProps({ chatId: [String, Number] })
const emit = defineEmits(['showProfileCard'])

const alert = useAlert()

const messages = ref([])
const loading = ref(false)
const currentPage = ref(0)
const showResultsModal = ref(false)
const showViewInChatModal = ref(false)
const selectedPoll = ref({})

let latest = null
let cancelToken = null
let everythingLoaded = false

const visibleCount = computed(() => VISIBLE_PAGE_SIZE * currentPage.value)
const visibleItems = computed(() =>
	pollItems.value.slice(0, visibleCount.value)
)

const pollItems = computed(() =>
	messages.value
		.map(({ id, user, poll }) => {
			// Calculate total vote count by summing all option vote counts
			const total_vote_count =
				poll.options?.reduce((total, option) => {
					return total + (option.vote_count || 0)
				}, 0) || 0
			return {
				...poll,
				messageId: id,
				user,
				total_vote_count,
			}
		})
		.flat()
)

const resultsLabel = computed(() => {
	if (selectedPoll.value.reveal_timing === 'after_vote') {
		return 'You can view poll results after voting'
	} else {
		return 'Poll results will be available after the poll closes'
	}
})

onMounted(() => {
	getMessageHistory()
})

function loadMoreMessages(isAtBottom) {
	if (!isAtBottom || !messages.value.length) return false
	getMessageHistory()
}

async function getMessageHistory() {
	if (loading.value || everythingLoaded) return
	try {
		loading.value = true

		if (pollItems.value.length > visibleCount.value) {
			// we already have more items loaded, just need to display them
			loading.value = false
			currentPage.value += 1
			return
		}

		const body = {
			bubble_id: props.chatId,
			polls: true,
		}
		if (latest) {
			body.latest = latest
		}

		if (cancelToken) {
			cancelToken.cancel()
		}

		cancelToken = getCancelToken()

		const data = await fetchHistoryWithoutStore(body, cancelToken)

		if (data.messages.length == 0) {
			everythingLoaded = true
			loading.value = false
			return
		}

		// Add polls to store here - BEFORE processing messages
		data.messages.forEach((message) => {
			if (message.poll) {
				setPoll(message.poll)
			}
		})

		messages.value = messages.value.concat(data.messages)
		currentPage.value += 1

		if (data.pagesize === data.messages.length) {
			latest = data.messages.at(-1).id
		} else {
			everythingLoaded = true
		}

		loading.value = false
	} catch (e) {
		console.error(e)
		alert.error(e)
	}
}

function onPollClick(poll) {
	selectedPoll.value = poll
	if (canViewResults(poll)) {
		showResultsModal.value = true
	} else {
		showViewInChatModal.value = true
	}
}

function closeModal() {
	showResultsModal.value = false
	showViewInChatModal.value = false
	selectedPoll.value = {}
}

function viewInChat() {
	router.push({
		name: 'chat',
		query: { centered: selectedPoll.value.messageId },
	})

	EventBus.$emit('closeGroupSettings')
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.group-media-polls {
	max-height: calc(100vh - 285px);
	padding-bottom: 40px;
	overflow-y: auto;

	:deep(.view-in-chat) {
		.left {
			flex: 1.2;
		}
	}

	.poll-card-stub {
		display: flex;
		width: 100%;
	}

	.poll-icon-container {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 42px;
		background-color: var(--neutral-100);
		border-right: 1px solid var(--neutral-200);
		flex-shrink: 0;
		border-top-left-radius: 6px;
		border-bottom-left-radius: 6px;
		.pronto-icon {
			font-size: 24px;
			color: var(--teal-300);
		}
	}

	.poll-content {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 52px;
		padding: 12px;
	}

	.poll-title {
		font-family: 'Avenir-Medium';
		font-size: 14px;
		color: var(--neutral-800);
		line-height: 1.3;
		margin-bottom: 8px;
	}

	.poll-footer {
		display: flex;
		align-items: center;
	}

	.poll-expiration {
		padding-right: 8px;
	}

	.poll-vote-count {
		padding-left: 8px;
		border-left: 1px solid var(--neutral-200);
	}

	.poll-expiration,
	.poll-vote-count {
		display: flex;
		align-items: center;
		gap: 4px;
		font-size: 12px;
		color: var(--neutral-400);

		.pronto-icon {
			font-size: 8px;
			flex-shrink: 0;
		}
	}
}

.empty-state {
	display: flex;
	width: 100%;
	height: 50vh;
	align-items: center;
	justify-content: center;
	font-family: 'Avenir-Medium';
	font-size: 28px;
	color: #4f5053;
	text-align: center;
}

.view-in-chat-modal-wrapper {
	height: 100%;
	padding: 16px 60px;
	@include flex-column-center;
	text-align: center;
	justify-content: space-around;
	.view-in-chat-icon {
		font-size: 60px;
		color: var(--blue-300);
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.group-media-polls {
		.ios-spinner {
			background: var(--neutral-100) !important;
		}
	}
}
</style>
